/**
 * Utility functions for formatting data
 */

export const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return 'N/A';
  return new Intl.NumberFormat('en-SG').format(value);
};

export const formatBlockNumber = (blockNumber: string | null): string => {
  if (!blockNumber) return 'N/A';
  // Check if block number is only digits
  if (/^\d+$/.test(blockNumber.trim())) {
    return `Block ${blockNumber}`;
  }
  // Otherwise return as-is
  return blockNumber;
};

export const parseUnitNumber = (unitNumber: string): { floor: number | null; stack: number | null } => {
  // Match pattern like #09-01 or 09-01
  const match = unitNumber.match(/#?(\d+)-(\d+)/);
  if (match) {
    return {
      floor: parseInt(match[1], 10),
      stack: parseInt(match[2], 10),
    };
  }
  return { floor: null, stack: null };
};

export const formatNotificationTime = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return date.toLocaleDateString();
};

/** True when a unit is sold (case-insensitive). */
export const isUnitStatusSold = (status: string | null | undefined): boolean =>
  (status ?? '').toString().trim().toLowerCase() === 'sold';

/** True when a unit has not been released yet (case-insensitive, tolerates "NOT_RELEASED"). */
export const isUnitStatusNotReleased = (status: string | null | undefined): boolean =>
  (status ?? '').toString().trim().toLowerCase().replace(/[_-]+/g, ' ') === 'not released';

/**
 * True when a unit's pricing must be hidden (sold or not released).
 * Sold units already hide price; not-released units must hide price/psf too.
 */
export const isUnitPricingHidden = (status: string | null | undefined): boolean =>
  isUnitStatusSold(status) || isUnitStatusNotReleased(status);

export const getUnitStatusColor = (status: string | null | undefined): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  if (!status) return 'default';
  const statusLower = status.toLowerCase();
  if (statusLower === 'available' || statusLower === 'open') return 'success';
  if (statusLower === 'sold' || statusLower === 'unavailable') return 'error';
  if (statusLower === 'reserved' || statusLower === 'on hold') return 'warning';
  return 'info';
};

export const getPhotoUrl = (photo?: string | null): string | undefined => {
  if (!photo) return undefined;
  if (/^https?:\/\//i.test(photo)) return photo;
  const photoPath = photo.startsWith('/') ? photo.slice(1) : photo;
  return `https://portal.huttonsgroup.com/${photoPath}`;
};
